
  import { defineComponent, onMounted, ref, computed, reactive } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment from 'moment';
  import Multiselect from '@vueform/multiselect';
  import {
    StatusNotesMap,
    StatusNotesLabels,
  } from '../registered-trades/status-notes.enum';
  import {
    TradeStatusMap,
    TradeStatusLabels,
  } from '../registered-trades/status-map.enum';

  export default defineComponent({
    name: 'registered-trades-reports',
    components: { Multiselect },
    async setup() {
      const showExportBtn = ref(false);
      const isLoading = ref(false);
      const hasQueried = ref(false);
      const data = ref();
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const defaultFilters = () => ({
        dateFrom: '',
        dateTo: '',
        areaIds: [],
        priceFrom: undefined,
        priceTo: undefined,
        sizeFrom: undefined,
        sizeTo: undefined,
        statusIds: [],
        statusNotesIds: [],
        propertyCategoryValues: [],
        propertyTypeIds: [],
      });

      const selectedItems = ref<any>(defaultFilters());

      const statusOptions = Object.entries(TradeStatusMap).map(
        ([label, value]) => ({ value, label })
      );

      const statusNotesOptions = Object.entries(StatusNotesMap).map(
        ([label, value]) => ({ value, label })
      );

      const areasComputed = computed(() => store.getters.allListAreas);
      const propertyTypesComputed = computed(
        () => store.getters.propertyTypesAllList
      );
      const registeredTradesDescComputed = computed(
        () => store.getters.getregisteredTradesDesc
      );

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const resetFilters = () => {
        selectedItems.value = defaultFilters();
        showExportBtn.value = false;
        hasQueried.value = false;
        data.value = undefined;
      };

      const previewLimit = 100;

      const previewData = computed(() => {
        if (!data.value?.data) return [];
        return data.value.data.slice(0, previewLimit);
      });

      const formatNumber = (val) => {
        if (val == null) return '-';
        return Number(val).toLocaleString();
      };

      const onSubmitCreate = async () => {
        if (selectedItems.value.dateFrom) {
          selectedItems.value.dateFrom = moment(
            selectedItems.value.dateFrom
          ).format('YYYY-MM-DD');
        }
        if (selectedItems.value.dateTo) {
          selectedItems.value.dateTo = moment(
            selectedItems.value.dateTo
          ).format('YYYY-MM-DD');
        }

        if (selectedItems.value.dateFrom && selectedItems.value.dateTo) {
          if (
            moment(selectedItems.value.dateTo).isBefore(
              moment(selectedItems.value.dateFrom)
            )
          ) {
            Swal.fire({
              text: translate('dateToMustBeAfterDateFrom'),
              icon: 'warning',
              confirmButtonText: translate('ok'),
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-light-primary' },
            });
            return;
          }
        }

        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        isLoading.value = true;
        hasQueried.value = true;

        await store.dispatch(
          Actions.GET_REGISTERED_TRADE_REPORTS,
          selectedItems.value
        );

        data.value = store.getters.registeredTradesReports;
        isLoading.value = false;

        if (data.value && data.value.data && data.value.data.length > 0) {
          showExportBtn.value = true;
        } else {
          showExportBtn.value = false;
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          isLoading.value = false;
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            submitButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
            submitButton.value!.disabled = false;
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('registeredTradesReports'), [
          translate('Reports'),
        ]);
      });

      await store.dispatch(Actions.GET_ALL_AREAS_LIST, {
        type: 'registeredTrades',
      });
      await store.dispatch(Actions.GET_ALL_PROPERTY_TYPE, {
        type: 'registeredTrades',
      });
      await store.dispatch(Actions.GET_REGISTERED_TRADES_DESC);

      const excelHeader = reactive({
        'Transaction Number': 'transactionNumber',
        Governorate: 'governorateName',
        Area: 'areaName',
        Size: 'size',
        description: 'descriptionAr',
        'Property Type': 'propertyTypeName',
        Price: 'price',
        'Price Per M2': 'pricePerM2',
        Block: 'block',
        Percentage: 'percentage',
        Notes: 'notesAr',
        'Transaction Date': {
          field: 'transactionDate',
          callback: (value) => {
            return value ? new Date(value).toISOString().slice(0, 10) : '';
          },
        },
        Status: 'statusName',
        'Status Notes': 'statusNotesName',
        'Block Count': 'blockCount',
        'Is Block Trade': 'isBlockTrade',
      });

      const excelHeaderRows = computed(() => {
        const all = 'الكل';
        const rows: string[] = [];

        const from = selectedItems.value.dateFrom || '-';
        const to = selectedItems.value.dateTo || '-';
        rows.push(`التاريخ: من ${from} إلى ${to}`);

        if (selectedItems.value.priceFrom || selectedItems.value.priceTo) {
          const pf = selectedItems.value.priceFrom || '-';
          const pt = selectedItems.value.priceTo || '-';
          rows.push(`السعر: من ${pf} إلى ${pt}`);
        }

        if (selectedItems.value.sizeFrom || selectedItems.value.sizeTo) {
          const sf = selectedItems.value.sizeFrom || '-';
          const st = selectedItems.value.sizeTo || '-';
          rows.push(`المساحة: من ${sf} إلى ${st}`);
        }

        if (
          selectedItems.value.propertyCategoryValues &&
          selectedItems.value.propertyCategoryValues.length > 0
        ) {
          rows.push(
            `فئة العقار: ${selectedItems.value.propertyCategoryValues.join(
              ', '
            )}`
          );
        } else {
          rows.push(`فئة العقار: ${all}`);
        }

        if (
          selectedItems.value.propertyTypeIds &&
          selectedItems.value.propertyTypeIds.length > 0
        ) {
          const labels = selectedItems.value.propertyTypeIds
            .map((id) => {
              const item = propertyTypesComputed.value.find((p) => p.id === id);
              return item ? item.title : id;
            })
            .join(', ');
          rows.push(`نوع العقار: ${labels}`);
        } else {
          rows.push(`نوع العقار: ${all}`);
        }

        if (
          selectedItems.value.areaIds &&
          selectedItems.value.areaIds.length > 0
        ) {
          const labels = selectedItems.value.areaIds
            .map((id) => {
              const item = areasComputed.value.find((a) => a.id === id);
              return item ? item.title : id;
            })
            .join(', ');
          rows.push(`المناطق: ${labels}`);
        } else {
          rows.push(`المناطق: ${all}`);
        }

        if (
          selectedItems.value.statusIds &&
          selectedItems.value.statusIds.length > 0
        ) {
          const labels = selectedItems.value.statusIds
            .map((val) => TradeStatusLabels[val] || val)
            .join(', ');
          rows.push(`الحالة: ${labels}`);
        } else {
          rows.push(`الحالة: ${all}`);
        }

        if (
          selectedItems.value.statusNotesIds &&
          selectedItems.value.statusNotesIds.length > 0
        ) {
          const labels = selectedItems.value.statusNotesIds
            .map((val) => StatusNotesLabels[val] || val)
            .join(', ');
          rows.push(`ملاحظات الحالة: ${labels}`);
        } else {
          rows.push(`ملاحظات الحالة: ${all}`);
        }

        return rows;
      });

      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        excelHeader,
        excelHeaderRows,
        showExportBtn,
        isLoading,
        hasQueried,
        data,
        selectedItems,
        areas: areasComputed,
        propertyTypes: propertyTypesComputed,
        registeredTradesDesc: registeredTradesDescComputed,
        onSubmitCreate,
        resetFilters,
        formatNumber,
        previewLimit,
        previewData,
        submitButton,
        translate,
        goBack,
        can,
        statusOptions,
        statusNotesOptions,
      };
    },
  });
